import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '../context/AuthContext'
import Overlay from './Overlay'
import MessageBox from './MessageBox'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

import ReceiptUploadModal from './ReceiptUploadModal'
import ElectricProofUploadModal from './ElectricProofUploadModal'
import NoChargingProofModal from './NoChargingProofModal'

import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ElectricProofInfoModal from './ElectricProofInfoModal'
import FuelProofUploadModal from './FuelProofUploadModal'
import ElectricCashbackInfoModal from './ElectricCashbackInfoModal'
import FuelCashbackInfoModal from './FuelCashbackInfoModal'

const fields = [
  ['date', 'TRIPLOG.RECEIPTS.TABLE.DATE'],
  ['time', 'TRIPLOG.RECEIPTS.TABLE.TIME'],
  ['vehicleId', 'TRIPLOG.RECEIPTS.TABLE.VEHICLE'],
  ['spz', 'vehicle_license_plate'],
  ['tripId', 'TRIPLOG.RECEIPTS.RELATED_TRIP'],
  ['fuel_type', 'TRIPLOG.RECEIPTS.TABLE.FUEL_TYPE'],
  ['charging_type', 'TRIPLOG.RECEIPTS.TABLE.CHARGING_TYPE'],
  ['fuel_name', 'TRIPLOG.RECEIPTS.TABLE.FUEL_NAME'],
  ['unit_price', 'TRIPLOG.RECEIPTS.TABLE.UNIT_PRICE'],
  ['units', 'TRIPLOG.RECEIPTS.TABLE.UNITS'],
  ['unit', 'TRIPLOG.RECEIPTS.TABLE.UNIT'],
  ['total_price', 'TRIPLOG.RECEIPTS.TABLE.TOTAL_PRICE'],
  ['currency', 'TRIPLOG.RECEIPTS.TABLE.CURRENCY'],
  ['vat_rate', 'TRIPLOG.RECEIPTS.TABLE.VAT_RATE'],
  ['receipt_number', 'TRIPLOG.RECEIPTS.TABLE.RECEIPT_NUMBER'],
  ['uid', 'TRIPLOG.RECEIPTS.TABLE.UID'],
  ['payment_type', 'TRIPLOG.RECEIPTS.TABLE.PAYMENT_TYPE'],
  ['transaction_id', 'TRIPLOG.RECEIPTS.TABLE.TRANSACTION_ID'],
  ['store_name', 'TRIPLOG.RECEIPTS.TABLE.STORE_NAME'],
  ['store_address', 'TRIPLOG.RECEIPTS.TABLE.STORE_ADDRESS'],
  ['ico', 'TRIPLOG.RECEIPTS.TABLE.ICO'],
  ['dic', 'TRIPLOG.RECEIPTS.TABLE.DIC'],
  ['note', 'TRIPLOG.RECEIPTS.TABLE.NOTES']
]

const buildReceiptDateTime = (dateValue, timeValue) => {
  if (!dateValue) return null

  const [year, month, day] = String(dateValue)
    .split('-')
    .map(Number)

  if (!year || !month || !day) return dateValue

  const [hours = 0, minutes = 0] = String(
    timeValue || '00:00'
  )
    .split(':')
    .map(Number)

  const date = new Date(
    year,
    month - 1,
    day,
    hours || 0,
    minutes || 0,
    0,
    0
  )

  return Number.isNaN(date.getTime())
    ? dateValue
    : date.toISOString()
}

const REQUIRED_FIELDS = [
  'date',
  'vehicleId',
  'units'
]

const Row = ({ label, children, layout }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
    <label
      style={{
        width: layout.labelW,
        fontWeight: 700,
        fontSize: layout.labelFont
      }}
    >
      {label}
    </label>

    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        width: layout.inputW
      }}
    >
      {children}
    </div>
  </div>
)

const ReceiptAddModal = ({
  open,
  onClose,
  onSaved,
  userId,
  initialTripId = '',
  lockTripSelection = false
}) => {
  const { t } = useTranslation()
  const { user } = useAuth()

  const [vw, setVw] = useState(window.innerWidth)
  const [membershipTypes, setMembershipTypes] = useState([])

  const [form, setForm] = useState({
    fuel_type: 'fuel',
    charging_type: null,
    unit: 'l'
  })

  const [vehiclesById, setVehiclesById] = useState({})
  const [trips, setTrips] = useState([])
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState(null)
  const [requiredDialog, setRequiredDialog] = useState(false)

const [pressedBtn, setPressedBtn] = useState(null)
const [hoverBtn, setHoverBtn] = useState(null)
const [uploadOpen, setUploadOpen] = useState(false)
const [electricUploadOpen, setElectricUploadOpen] = useState(false)
const [fuelUploadOpen, setFuelUploadOpen] = useState(false)
const [noProofConfirmOpen, setNoProofConfirmOpen] = useState(false)
const [electricProofInfoOpen, setElectricProofInfoOpen] = useState(false)
const [electricCashbackInfoOpen, setElectricCashbackInfoOpen] = useState(false)
const [fuelCashbackInfoOpen, setFuelCashbackInfoOpen] = useState(false)

const [electricProofUploadId, setElectricProofUploadId] = useState(null)
const [fuelProofUploadId, setFuelProofUploadId] = useState(null)

const [showElectricProofHint] = useState(false)
const [electricProofHintPos] = useState({
  x: 0,
  y: 0
})

  let layout = {}

  if (vw >= 1920) {
    layout = {
      labelW: 150,
      inputW: 500,
      inputH: 20,
      labelFont: 15,
      inputFont: 14,
      rowGap: 4
    }
  } else if (vw >= 1366) {
    layout = {
      labelW: 130,
      inputW: 500,
      inputH: 18,
      labelFont: 14,
      inputFont: 13,
      rowGap: 3
    }
  } else {
    layout = {
      labelW: 120,
      inputW: 240,
      inputH: 18,
      labelFont: 13,
      inputFont: 12,
      rowGap: 3
    }
  }

  const modalMaxWidth =
    vw >= 2800
      ? '19.7vw'
      : vw >= 2600
        ? '24vw'
        : vw >= 2400
          ? '26vw'
          : vw >= 1920
            ? '32vw'
            : vw >= 1366
              ? '44vw'
              : '35vw'

  const modalMaxHeight =
    vw >= 1920 ? '95vh' : '95vh'

  const modalTitleFontSize =
    vw >= 1920 ? 24 : vw >= 1366 ? 24 : 18

  const inputStyle = {
    height: layout.inputH,
    fontSize: layout.inputFont,
    padding: '6px 10px',
    borderRadius: 6,
    border: '1px solid #999',
    width: '100%'
  }

  useEffect(() => {
    const handleResize = () => setVw(window.innerWidth)
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    if (!open) return

    const loadMembershipTypes = async () => {
      try {
        const res = await fetchWithAuth('/membership/types')

        if (!res.ok) {
          setMembershipTypes([])
          return
        }

        const data = await res.json()

        setMembershipTypes(
          Array.isArray(data) ? data : []
        )
      } catch {
        setMembershipTypes([])
      }
    }

    loadMembershipTypes()
  }, [open])

  const currentMembershipType = useMemo(() => {
    if (!user?.membership_type_id) {
      return null
    }

    return (
      membershipTypes.find(
        item =>
          Number(item.id) ===
          Number(user.membership_type_id)
      ) || null
    )
  }, [
    membershipTypes,
    user?.membership_type_id
  ])

  const hasActiveMembership =
    Boolean(currentMembershipType)

  const forcedFuelType =
    currentMembershipType?.is_electric
      ? 'electric'
      : 'fuel'

  const forcedUnit =
    forcedFuelType === 'electric'
      ? 'kWh'
      : 'l'

  useEffect(() => {
    if (!open) return

    const fuelType =
      hasActiveMembership
        ? forcedFuelType
        : 'fuel'

    setForm({
      fuel_type: fuelType,
      charging_type:
        fuelType === 'electric'
          ? ''
          : null,
      unit:
        fuelType === 'electric'
          ? 'kWh'
          : 'l',
      tripId:
        initialTripId
          ? String(initialTripId)
          : ''
    })

setVehiclesById({})
setErrors({})
setMessage(null)
setRequiredDialog(false)
setElectricProofUploadId(null)
setFuelProofUploadId(null)

  }, [
    open,
    initialTripId,
    hasActiveMembership,
    forcedFuelType
  ])

  useEffect(() => {
    if (!open) return
    if (!hasActiveMembership) return

    setForm(prev => ({
      ...prev,
      fuel_type: forcedFuelType,
      charging_type:
        forcedFuelType === 'electric'
          ? (prev.charging_type || '')
          : null,
      unit: forcedUnit
    }))
  }, [
    open,
    hasActiveMembership,
    forcedFuelType,
    forcedUnit
  ])

  const loadVehicles = useCallback(async () => {
    if (!userId) return

    try {
      const res =
        await fetchWithAuth(
          `/users/${userId}/vehicles`
        )

      if (!res.ok) return

      const data = await res.json()

      if (!Array.isArray(data)) {
        return
      }

      const index = data.reduce(
        (acc, vehicle) => {
          if (!vehicle?.id) {
            return acc
          }

          acc[String(vehicle.id)] =
            vehicle

          return acc
        },
        {}
      )

      setVehiclesById(index)
    } catch {}
  }, [userId])

  const loadTrips = useCallback(async () => {
    try {
      const res =
        await fetchWithAuth('/triplog/trips')

      if (!res.ok) return

      const data = await res.json()

      if (!Array.isArray(data)) {
        return
      }

      setTrips(data)
    } catch {}
  }, [])

  const filteredTrips = useMemo(() => {
    if (!form.vehicleId) {
      return []
    }

    return trips.filter(trip => {
      if (
        String(trip?.vehicleId) !==
        String(form.vehicleId)
      ) {
        return false
      }

      const assignedReceiptId =
        trip?.receiptId ??
        trip?.receipt_id ??
        null

      return !assignedReceiptId
    })
  }, [
    form.vehicleId,
    trips
  ])

  useEffect(() => {
    if (!open) return

    loadVehicles()
    loadTrips()
  }, [
    open,
    loadVehicles,
    loadTrips
  ])

  useEffect(() => {
    const vehicles =
      Object.values(vehiclesById)

    if (vehicles.length !== 1) return
    if (form.vehicleId) return

    const vehicle = vehicles[0]

    setForm(prev => ({
      ...prev,
      vehicleId:
        String(vehicle.id)
    }))
  }, [
    vehiclesById,
    form.vehicleId
  ])

useEffect(() => {
  const handleKeyDown = e => {
    if (
      e.key === 'Escape' &&
      open &&
      !uploadOpen &&
      !noProofConfirmOpen &&
      !electricProofInfoOpen &&
      !electricCashbackInfoOpen &&
      !fuelCashbackInfoOpen
    ) {
      onClose()
    }
  }

  window.addEventListener(
    'keydown',
    handleKeyDown
  )

  return () => {
    window.removeEventListener(
      'keydown',
      handleKeyDown
    )
  }
}, [
  open,
  onClose,
  uploadOpen,
  noProofConfirmOpen,
  electricProofInfoOpen,
  electricCashbackInfoOpen,
  fuelCashbackInfoOpen
])

  useEffect(() => {
    if (lockTripSelection) return
    if (!form.tripId) return

    const selectedTrip =
      trips.find(
        trip =>
          String(trip.id) ===
          String(form.tripId)
      )

    if (
      selectedTrip &&
      String(selectedTrip.vehicleId) !==
        String(form.vehicleId)
    ) {
      setForm(prev => ({
        ...prev,
        tripId: ''
      }))
    }
  }, [
    form.vehicleId,
    form.tripId,
    trips,
    lockTripSelection
  ])

  useEffect(() => {
    if (!open) return

    if (
      !lockTripSelection ||
      !initialTripId
    ) {
      return
    }

    const selectedTrip =
      trips.find(
        trip =>
          String(trip.id) ===
          String(initialTripId)
      )

    if (!selectedTrip) return

    const selectedVehicle =
      vehiclesById[
        String(selectedTrip.vehicleId)
      ]

    const plate =
      selectedVehicle?.licensePlate ||
      selectedVehicle?.spz ||
      ''

    setForm(prev => ({
      ...prev,
      tripId:
        String(initialTripId),
      vehicleId:
        String(
          selectedTrip.vehicleId || ''
        ),
      spz: plate
    }))
  }, [
    open,
    lockTripSelection,
    initialTripId,
    trips,
    vehiclesById
  ])

const validate = () => {
  const newErrors = {}

  REQUIRED_FIELDS.forEach(field => {
    if (
      !form[field] ||
      String(form[field]).trim() === ''
    ) {
      newErrors[field] = true
    }
  })

  const unitsNum =
    Number(form.units)

  if (
    form.units === '' ||
    form.units === null ||
    form.units === undefined ||
    Number.isNaN(unitsNum) ||
    unitsNum <= 0
  ) {
    newErrors.units = true
  }

  const unitPriceNum =
    Number(form.unit_price)

if (
  form.fuel_type === 'electric'
) {
  if (!form.charging_type) {
    newErrors.charging_type = true
  }

    const zeroPriceAllowed =
      form.charging_type === 'home' ||
      form.charging_type === 'package'

if (
  zeroPriceAllowed
    ? (
        form.unit_price !== '' &&
        form.unit_price !== null &&
        form.unit_price !== undefined &&
        (
          Number.isNaN(unitPriceNum) ||
          unitPriceNum < 0
        )
      )
    : (
        form.unit_price === '' ||
        form.unit_price === null ||
        form.unit_price === undefined ||
        Number.isNaN(unitPriceNum) ||
        unitPriceNum <= 0
      )
) {
  newErrors.unit_price = true
}
  } else {
    

    if (
      !form.unit_price ||
      Number.isNaN(unitPriceNum) ||
      unitPriceNum <= 0
    ) {
      newErrors.unit_price = true
    }
  }

  setErrors(newErrors)

if (
  Object.keys(newErrors).length > 0
) {
  setRequiredDialog(true)
  return false
}

  return true
}

const saveReceipt = async () => {
  if (!validate()) return

  const proofAllowed =
    !hasActiveMembership ||
    forcedFuelType === form.fuel_type

  if (
    proofAllowed &&
    form.fuel_type === 'electric' &&
    !electricProofUploadId
  ) {
    setNoProofConfirmOpen(true)
    return
  }

  if (
    proofAllowed &&
    form.fuel_type === 'fuel' &&
    !fuelProofUploadId
  ) {
    setNoProofConfirmOpen(true)
    return
  }

  await performSaveReceipt()
}

const performSaveReceipt = async (
  proofUploadId,
  fuelProofId
) => {
  try {
    setLoading(true)

    const unitPriceNum =
      Number(form.unit_price || 0)

    const unitsNum =
      Number(form.units || 0)

    const calculatedTotal =
      unitPriceNum * unitsNum

    const {
      time,
      ...formWithoutTime
    } = form

const payload = {
  ...formWithoutTime,

  tripId:
    form.tripId
      ? Number(form.tripId)
      : null,

  total_price:
    calculatedTotal,

  unit_price:
    unitPriceNum,

electric_proof_upload_id:
  form.fuel_type === 'electric'
    ? (proofUploadId || electricProofUploadId)
    : undefined,

fuel_proof_upload_id:
  form.fuel_type === 'fuel'
    ? (fuelProofId || fuelProofUploadId)
    : undefined,

  date:
    buildReceiptDateTime(
      form.date,
      time
    )
}

    const res =
      await fetchWithAuth(
        '/triplog/receipts/manual',
        {
          method: 'POST',
          headers: {
            'Content-Type':
              'application/json'
          },
          body:
            JSON.stringify(payload)
        }
      )

    if (!res.ok) {
      let err = null

      try {
        err = await res.json()
      } catch {}

      setMessage({
        message:
          err?.message ||
          t('SAVE_FAILED'),
        type: 'error'
      })

      return
    }

    const created =
      await res
        .json()
        .catch(() => payload)

    setMessage({
      message: t('SAVED'),
      type: 'success'
    })

    onSaved?.(created)
    onClose()
  } catch {
    setMessage({
      message:
        t('CONNECTION_ERROR'),
      type: 'error'
    })
  } finally {
    setLoading(false)
  }
}

  if (!open) return null

  return (
    <>
      <Overlay
        open={loading}
        zIndex={4000}
        backdropOpacity={0.75}
      />

<NoChargingProofModal
  open={noProofConfirmOpen}
  onClose={() => {
    setNoProofConfirmOpen(false)
  }}
  onUpload={() => {
    setNoProofConfirmOpen(false)

    if (form.fuel_type === 'electric') {
      setElectricUploadOpen(true)
      return
    }

    setFuelUploadOpen(true)
  }}
  onConfirm={async () => {
    setNoProofConfirmOpen(false)
    await performSaveReceipt()
  }}
/>

<ElectricProofInfoModal
  open={electricProofInfoOpen}
  onClose={() => {
    setElectricProofInfoOpen(false)
  }}
/>

<ElectricCashbackInfoModal
  open={electricCashbackInfoOpen}
  onClose={() => {
    setElectricCashbackInfoOpen(false)
  }}
/>

<FuelCashbackInfoModal
  open={fuelCashbackInfoOpen}
  onClose={() => {
    setFuelCashbackInfoOpen(false)
  }}
/>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background:
            'rgba(0,0,0,0.4)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 3100,
          padding: 20
        }}
      >
        <div
          onClick={e =>
            e.stopPropagation()
          }
          style={{
            background: '#ededed',
	    position: 'relative',
            padding:
              '5px 30px 10px 30px',
            border:
              '2px solid #107c6b',
            borderRadius: 14,
            boxShadow:
              '0 12px 28px rgba(0,0,0,0.65)',
            width: '100%',
            maxWidth:
              modalMaxWidth,
            maxHeight:
              modalMaxHeight,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}
        >
<button
  type="button"
  style={{
    ...modalPrimaryButtonStyle(
      pressedBtn,
      form.fuel_type === 'electric'
        ? 'electricCashbackInfo'
        : 'fuelCashbackInfo',
      hoverBtn === (
        form.fuel_type === 'electric'
          ? 'electricCashbackInfo'
          : 'fuelCashbackInfo'
      )
    ),
    position: 'absolute',
    top: 10,
    right: 12,
    width: 42,
    height: 42,
    minWidth: 42,
    padding: 0,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  }}
  onMouseEnter={() =>
    setHoverBtn(
      form.fuel_type === 'electric'
        ? 'electricCashbackInfo'
        : 'fuelCashbackInfo'
    )
  }
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() =>
    setPressedBtn(
      form.fuel_type === 'electric'
        ? 'electricCashbackInfo'
        : 'fuelCashbackInfo'
    )
  }
  onMouseUp={() =>
    setPressedBtn(null)
  }
  onClick={() => {
    if (form.fuel_type === 'electric') {
      setElectricCashbackInfoOpen(true)
      return
    }

    setFuelCashbackInfoOpen(true)
  }}
>
  <InfoOutlinedIcon fontSize="small" />
</button>
          <h3
            style={{
              fontSize:
                modalTitleFontSize,
              marginTop: 5,
              marginBottom: 20
            }}
          >
            {t(
              'TRIPLOG.RECEIPTS.ADD_RECEIPT_TITLE'
            )}
          </h3>

          {requiredDialog && (
            <MessageBox
              message={t(
                'TRIPLOG.FORM.REQUIRED_FIELDS'
              )}
              type="error"
            />
          )}

          {message && (
            <MessageBox
              message={message.message}
              type={message.type}
            />
          )}

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: layout.rowGap,
              overflowY: 'auto',
              flex: 1
            }}
          >
            {fields.map(
              ([key, labelKey]) => {
                const vehicle =
                  vehiclesById[
                    String(
                      form.vehicleId
                    )
                  ]

                if (
                  key ===
                    'charging_type' &&
                  form.fuel_type !==
                    'electric'
                ) {
                  return null
                }

                return (
                  <Row
                    key={key}
                    label={t(labelKey)}
                    layout={layout}
                  >
                    {key ===
                    'vehicleId' ? (
                      <select
                        value={
                          form.vehicleId ||
                          ''
                        }
                        onChange={e => {
                          const vehicleId =
                            e.target
                              .value ||
                            ''

                          const nextVehicle =
                            vehiclesById[
                              String(
                                vehicleId
                              )
                            ]

                          const plate =
                            nextVehicle
                              ?.licensePlate ||
                            nextVehicle
                              ?.spz ||
                            ''

                          setErrors(
                            prev => ({
                              ...prev,
                              vehicleId:
                                false
                            })
                          )

                          setForm(
                            prev => ({
                              ...prev,
                              vehicleId,
                              tripId: '',
                              spz: plate
                            })
                          )
                        }}
                        style={{
                          ...inputStyle,
                          height: 34,
                          background:
                            errors.vehicleId
                              ? '#ffdddd'
                              : '#fff'
                        }}
                      >
                        <option value="">
                          --
                        </option>

                        {Object.values(
                          vehiclesById
                        ).map(v => (
                          <option
                            key={v.id}
                            value={v.id}
                          >
                            {`${v.name || v.model || '-'} (${v.licensePlate || v.spz || '-'})`}
                          </option>
                        ))}
                      </select>
                    ) : key ===
                      'tripId' ? (
                      <select
                        value={
                          form.tripId ||
                          ''
                        }
                        onChange={e =>
                          setForm(
                            prev => ({
                              ...prev,
                              tripId:
                                e.target
                                  .value ||
                                ''
                            })
                          )
                        }
                        disabled={
                          lockTripSelection
                        }
                        style={{
                          ...inputStyle,
                          height: 34,
                          background:
                            lockTripSelection
                              ? '#ddd'
                              : '#fff'
                        }}
                      >
                        <option value="">
                          --
                        </option>

                        {filteredTrips.map(
                          trip => {
                            const date =
                              trip.date
                                ? new Date(
                                    trip.date
                                  ).toLocaleDateString(
                                    'sk-SK'
                                  )
                                : '-'

                            const vehicle =
                              vehiclesById[
                                String(
                                  trip.vehicleId
                                )
                              ]

                            const vehicleLabel =
                              vehicle?.name ||
                              vehicle?.model ||
                              vehicle
                                ?.licensePlate ||
                              vehicle?.spz ||
                              '-'

                            const spz =
                              vehicle
                                ?.licensePlate ||
                              vehicle?.spz ||
                              '-'

                            return (
                              <option
                                key={
                                  trip.id
                                }
                                value={
                                  trip.id
                                }
                              >
                                {`#${trip.tripNumber} · ${date} · ${vehicleLabel} · ${spz} · ${trip.startPlace || '-'} → ${trip.endPlace || '-'}`}
                              </option>
                            )
                          }
                        )}
                      </select>
                    ) : key ===
                      'fuel_type' ? (
<select
  value={
    form.fuel_type ||
    'fuel'
  }
  onChange={e => {
    const fuelType =
      e.target.value

    setForm(
      prev => ({
        ...prev,

        fuel_type:
          fuelType,

        charging_type:
          fuelType ===
          'electric'
            ? (
                prev.charging_type ||
                ''
              )
            : null,

        unit:
          fuelType ===
          'electric'
            ? 'kWh'
            : 'l'
      })
    )

    setErrors(
      prev => ({
        ...prev,
        fuel_type: false,
        charging_type: false,
        unit_price: false
      })
    )
  }}
                        style={{
                          ...inputStyle,
                          height: 34,
background: '#fff',
cursor: 'pointer'
                        }}
                      >
                        <option value="fuel">
                          {t(
                            'TRIPLOG.RECEIPTS.TABLE.FUEL'
                          )}
                        </option>

                        <option value="electric">
                          {t(
                            'TRIPLOG.RECEIPTS.TABLE.ELECTRIC'
                          )}
                        </option>
                      </select>
                    ) : key ===
                      'charging_type' ? (
                      <select
                        value={
                          form.charging_type ||
                          ''
                        }
                        onChange={e => {
                          const chargingType =
                            e.target
                              .value

                          setForm(
                            prev => ({
                              ...prev,
                              charging_type:
                                chargingType
                            })
                          )

                          setErrors(
                            prev => ({
                              ...prev,
                              charging_type:
                                false,
                              unit_price:
                                false
                            })
                          )
                        }}
                        style={{
                          ...inputStyle,
                          height: 34,
                          background:
                            errors.charging_type
                              ? '#ffdddd'
                              : '#fff'
                        }}
                      >
                        <option value="">
                          {t(
                            'TRIPLOG.RECEIPTS.TABLE.CHARGING_TYPE_SELECT'
                          )}
                        </option>

                        <option value="public">
                          {t(
                            'TRIPLOG.RECEIPTS.TABLE.CHARGING_PUBLIC'
                          )}
                        </option>

                        <option value="home">
                          {t(
                            'TRIPLOG.RECEIPTS.TABLE.CHARGING_HOME'
                          )}
                        </option>

                        <option value="package">
                          {t(
                            'TRIPLOG.RECEIPTS.TABLE.CHARGING_PACKAGE'
                          )}
                        </option>
                      </select>
                    ) : key ===
                      'currency' ? (
                      <select
                        value={
                          form.currency ||
                          'EUR'
                        }
                        onChange={e => {
                          const val =
                            e.target
                              .value

                          setErrors(
                            prev => ({
                              ...prev,
                              currency:
                                false
                            })
                          )

                          setForm(
                            prev => ({
                              ...prev,
                              currency:
                                val
                            })
                          )
                        }}
                        style={{
                          ...inputStyle,
                          height: 34,
                          background:
                            errors.currency
                              ? '#ffdddd'
                              : '#fff'
                        }}
                      >
                        <option value="EUR">
                          EUR
                        </option>

                        <option value="GBP">
                          GBP
                        </option>

                        <option value="USD">
                          USD
                        </option>

                        <option value="PLN">
                          PLN
                        </option>

                        <option value="CZK">
                          CZK
                        </option>

                        <option value="UAH">
                          UAH
                        </option>
                      </select>
                    ) : key ===
                      'unit' ? (
                      <input
                        type="text"
                        value={
                          form.unit ||
                          ''
                        }
                        disabled
                        style={{
                          ...inputStyle,
                          background:
                            '#ddd',
                          cursor:
                            'not-allowed'
                        }}
                      />
                    ) : (
			<input
			type={
 			 key === 'date'
 			   ? 'date'
  			  : key === 'time'
   			   ? 'time'
   			   : 'text'
			}
                        value={
                          key === 'spz'
                            ? (
                                vehicle
                                  ?.licensePlate ||
                                vehicle
                                  ?.spz ||
                                ''
                              )
                            : (
                                form[
                                  key
                                ] || ''
                              )
                        }
                        onChange={e => {
                          let val =
                            e.target
                              .value

			if (key === 'date' && val) {
 			 const now = new Date()

  			setForm(prev => ({
  			  ...prev,
   			 date: val,
   			 time:
   			   prev.time ||
      			`${String(now.getHours()).padStart(2, '0')}:${String(
     			   now.getMinutes()
   			   ).padStart(2, '0')}`
 			 }))

  			setErrors(prev => ({
  			  ...prev,
   			 date: false
  			}))

			  return
			}

                          if (
                            key ===
                              'unit_price' ||
                            key ===
                              'units' ||
                            key ===
                              'vat_rate'
                          ) {
                            val = val
                              .replace(
                                ',',
                                '.'
                              )
                              .replace(
                                /[^0-9.]/g,
                                ''
                              )
                              .replace(
                                /(\..*)\./g,
                                '$1'
                              )

                            if (
                              val.includes(
                                '.'
                              )
                            ) {
                              const [
                                i,
                                d
                              ] =
                                val.split(
                                  '.'
                                )

                              val =
                                i +
                                '.' +
                                (
                                  d ||
                                  ''
                                ).slice(
                                  0,
                                  2
                                )
                            }
                          }

                          setForm(
                            prev => {
                              const next =
                                {
                                  ...prev,
                                  [key]:
                                    val
                                }

                              if (
                                key ===
                                  'unit_price' ||
                                key ===
                                  'units' ||
                                key ===
                                  'vat_rate'
                              ) {
                                const unit =
                                  parseFloat(
                                    next.unit_price
                                  ) || 0

                                const qty =
                                  parseFloat(
                                    next.units
                                  ) || 0

                                next.total_price =
                                  next.unit_price !==
                                    '' &&
                                  next.units !==
                                    ''
                                    ? (
                                        Math.round(
                                          unit *
                                            qty *
                                            100
                                        ) /
                                        100
                                      ).toFixed(
                                        2
                                      )
                                    : ''
                              }

                              return next
                            }
                          )

                          if (
                            key ===
                            'unit_price'
                          ) {
                            const num =
                              parseFloat(
                                val
                              )

                            const zeroPriceAllowed =
                              form.fuel_type ===
                                'electric' &&
                              (
                                form.charging_type ===
                                  'home' ||
                                form.charging_type ===
                                  'package'
                              )

                            setErrors(
                              prev => ({
                                ...prev,

                                unit_price:
                                  val ===
                                    '' ||
                                  isNaN(
                                    num
                                  ) ||
                                  (
                                    zeroPriceAllowed
                                      ? num <
                                        0
                                      : num <=
                                        0
                                  )
                              })
                            )
                          } else if (
                            key ===
                            'units'
                          ) {
                            const num =
                              parseFloat(
                                val
                              )

                            setErrors(
                              prev => ({
                                ...prev,
                                units:
                                  !val ||
                                  isNaN(
                                    num
                                  ) ||
                                  num <=
                                    0
                              })
                            )
                          } else {
                            setErrors(
                              prev => ({
                                ...prev,
                                [key]:
                                  false
                              })
                            )
                          }
                        }}
                        onClick={e => {
			if (
 			 (key === 'date' || key === 'time') &&
  			e.target.showPicker
			) {
			  e.target.showPicker()
			}
                        }}
                        disabled={
                          key ===
                            'spz' ||
                          key ===
                            'total_price'
                        }
                        style={{
                          ...inputStyle,
                          border:
                            errors[key]
                              ? '1px solid #c62828'
                              : inputStyle.border,
                          background:
                            key ===
                              'spz' ||
                            key ===
                              'total_price'
                              ? '#ddd'
                              : errors[
                                    key
                                  ]
                                ? 'rgba(255,0,0,0.1)'
                                : '#fff'
                        }}
                      />
                    )}
                  </Row>
                )
              }
            )}
          </div>

          <div
            style={{
              display: 'flex',
              gap: 12,
              marginTop: 15,
              justifyContent: 'center'
            }}
          >



{showElectricProofHint && form.fuel_type === 'electric' && (
  <div
    style={{
      position: 'fixed',
      top: electricProofHintPos.y - 60,
      left: electricProofHintPos.x,
      transform: 'translateX(-50%)',
      background: 'rgba(0,0,0,0.85)',
      color: '#E7DFEC',
      padding: '6px 14px 8px 14px',
      borderRadius: 12,
      fontSize: 16,
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: 5000,
      border: '1.5px solid #1e3f44',
      boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
      display: 'flex',
      alignItems: 'center',
      gap: 6,
      lineHeight: 1
    }}
  >
    <span>
      {t('TRIPLOG.RECEIPTS.TABLE.ELECTRIC_PROOF_TOOLTIP')}
    </span>

    <span
      style={{
        width: 18,
        height: 18,
        border: '1.5px solid #E7DFEC',
        borderRadius: '50%',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 12,
        fontWeight: 700,
        fontStyle: 'normal'
      }}
    >
      i
    </span>
  </div>
)}

<button
  style={
    modalPrimaryButtonStyle(
      pressedBtn,
      'saveReceipt',
      hoverBtn ===
        'saveReceipt'
    )
  }
  onMouseEnter={() =>
    setHoverBtn(
      'saveReceipt'
    )
  }
  onMouseLeave={() => {
    setHoverBtn(null)
    setPressedBtn(null)
  }}
  onMouseDown={() =>
    setPressedBtn(
      'saveReceipt'
    )
  }
  onMouseUp={() =>
    setPressedBtn(null)
  }
  onClick={() => {
    const canUploadFuelReceipt =
      form.fuel_type === 'fuel' &&
      (
        !hasActiveMembership ||
        forcedFuelType === 'fuel'
      )

    if (
      canUploadFuelReceipt &&
      !form.date
    ) {
      setUploadOpen(true)
      return
    }

    saveReceipt()
  }}
>
  {
    form.fuel_type === 'fuel' &&
    (
      !hasActiveMembership ||
      forcedFuelType === 'fuel'
    ) &&
    !form.date
      ? t(
          'TRIPLOG.VEHICLES.BUTTON_UPLOAD_RECEIPT_BUTTON'
        )
      : t('SAVE')
  }
</button>

            <button
              style={
                modalSecondaryButtonStyle(
                  pressedBtn,
                  'cancelReceipt',
                  hoverBtn ===
                    'cancelReceipt'
                )
              }
              onMouseEnter={() =>
                setHoverBtn(
                  'cancelReceipt'
                )
              }
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() =>
                setPressedBtn(
                  'cancelReceipt'
                )
              }
              onMouseUp={() =>
                setPressedBtn(null)
              }
              onClick={onClose}
            >
              {t('CANCEL')}
            </button>
          </div>
        </div>
      </div>

      <ReceiptUploadModal
        open={uploadOpen}
        userId={userId}
        initialTripId={
          form.tripId || ''
        }
        initialVehicleId={
          form.vehicleId || ''
        }
        lockTripSelection={
          lockTripSelection
        }
        onClose={() =>
          setUploadOpen(false)
        }
        onSaved={() => {
          setUploadOpen(false)

          onSaved?.({
            source: 'upload',
            delayMs: 8000
          })
        }}
      />
<ElectricProofUploadModal
  open={electricUploadOpen}
  onClose={() => {
    setElectricUploadOpen(false)
    setNoProofConfirmOpen(true)
  }}
  onUploaded={async uploadId => {
    setElectricProofUploadId(uploadId)
    setElectricUploadOpen(false)

    await performSaveReceipt(uploadId)
  }}
/>
<FuelProofUploadModal
  open={fuelUploadOpen}
  onClose={() => {
    setFuelUploadOpen(false)
    setNoProofConfirmOpen(true)
  }}
  onUploaded={async uploadId => {
    setFuelProofUploadId(uploadId)
    setFuelUploadOpen(false)

    await performSaveReceipt(
      undefined,
      uploadId
    )
  }}
/>
    </>
  )
}

export default ReceiptAddModal